import React from 'react';
import PropTypes from 'prop-types';
import FieldConstraintForm from './FieldConstraintForm';
import DanglingRefRow from './DanglingRefRow';
import { getBranchFieldWarnings } from '../../dsv-schema-utils';

/**
 * The ONE default requiredness for a branch row the author creates in the UI.
 *
 * `true` on purpose, and it is the only defaulting that happens anywhere:
 *  - Inside a conditional branch, "when the IF matches, this field must be
 *    provided AND look like X" is what an author ticking a field means. The
 *    opposite default ("constrain the value but don't require the field") is the
 *    exact shape of the lift_datahub 1.0.0 production defect.
 *  - It is the safe direction: over-requiring is caught by the author on the
 *    next submission, under-requiring silently weakens a live contract.
 *
 * The HYDRATION path deliberately does NOT use this default — it reads
 * requiredness from the stored branch's own `required` array (see
 * serialization.js parseBranch). Defaulting hydrated rows to `true` would emit
 * `required` where the stored schema had none, i.e. silently rewrite live
 * contracts on an unedited load-and-save. Instead of a second default, the
 * divergence is made VISIBLE: `getBranchFieldWarnings` flags any row that states
 * value constraints without requiring the field — wherever the row came from —
 * and the branch renders that advice inline, while `normalizeRequiredness`
 * guarantees the checkbox always shows the requiredness the emitter will write.
 */
export const FRESH_ROW_REQUIRED_DEFAULT = true;

export function newFieldConstraint(fieldName, addressable) {
	const constraint = {
		fieldName,
		required: FRESH_ROW_REQUIRED_DEFAULT,
		type: addressable?.dataType || 'string',
	};
	// For array fields, carry the base schema's item type through so
	// validateFieldConstraints doesn't flag a missing arrayItemType.
	if (addressable?.dataType === 'array' && addressable?.arrayItemType) {
		constraint.arrayItemType = addressable.arrayItemType;
	}
	if (addressable?.format) constraint.format = addressable.format;
	return constraint;
}

/**
 * Present a row with the requiredness the EMITTER will actually use.
 *
 * `emitBranch` requires a leaf whenever `required !== false`, so a row whose
 * `required` is absent (hand-authored rule JSON, a programmatic edit, a legacy
 * shape) emits as MANDATORY while the checkbox would render unticked — the
 * displayed state and the saved semantics disagree, which is the whole class of
 * bug this defect is about. Coercing to an explicit boolean here makes the
 * control truthful; the normalized row is what `FieldConstraintForm` echoes back
 * through `onChange`, so the stored rule converges on the first edit.
 *
 * Rows that already carry a boolean are returned untouched (no new object), so
 * this can never perturb a hydrated rule or its round-trip.
 */
function normalizeRequiredness(field) {
	if (typeof field.required === 'boolean') return field;
	return { ...field, required: field.required !== false };
}

/**
 * Group addressable entries by top-level parent so the chip grid renders
 * with a visual header per nest. Preserves input order within each group
 * (paths.js already emits containers before children).
 */
function groupByTopLevel(fields) {
	const groups = new Map();
	for (const f of fields) {
		const groupKey = f.topLevel || f.displayPath;
		if (!groups.has(groupKey)) groups.set(groupKey, []);
		groups.get(groupKey).push(f);
	}
	return groups;
}

/**
 * Shared editor for THEN and ELSE field lists. Renders a checkbox column of
 * available top-level fields + per-selected-field `FieldConstraintForm`.
 * Fields referenced by the branch but missing from the current schema render
 * as `DanglingRefRow` instead.
 */
export default function BranchFieldsEditor({
	branch,
	ruleIndex,
	selectedFields,
	availableFields,
	errors,
	onChange,
}) {
	// Any schema field may be constrained in THEN / ELSE — including fields
	// referenced in the IF clause (e.g. when `sss` equals 2, additionally
	// require `sss` to match a pattern is a valid x-allOf pattern).
	const selectable = availableFields;
	const availableNames = new Set(selectable.map((f) => f.displayPath));
	const selectedNames = new Set(selectedFields.map((f) => f.fieldName));
	const groups = groupByTopLevel(selectable);
	// Fields flagged `atLeastOne` emit as one `anyOf: [{ required: [...] }, ...]`
	// group on the branch, so the semantics only make sense for the group as a
	// whole — surface a branch-level hint alongside the per-field toggles.
	const atLeastOneCount = selectedFields.filter((f) => f.atLeastOne).length;

	const toggle = (displayPath) => {
		if (selectedNames.has(displayPath)) {
			onChange(selectedFields.filter((f) => f.fieldName !== displayPath));
			return;
		}
		const addressable = availableFields.find((f) => f.displayPath === displayPath);
		onChange([...selectedFields, newFieldConstraint(displayPath, addressable)]);
	};

	const updateField = (nextField) => {
		// Once the user authors a constraint or changes the type on a stub
		// that parseBranch synthesized from a bare `required` array, the
		// field needs to round-trip as a real `properties` entry on emit.
		// Strip the flag so emitBranch stops skipping it.
		const { synthesizedFromRequired: _flag, ...promoted } = nextField;
		onChange(selectedFields.map((f) =>
			f.fieldName === nextField.fieldName ? promoted : f
		));
	};

	const removeField = (name) => {
		onChange(selectedFields.filter((f) => f.fieldName !== name));
	};

	const replaceField = (oldName, newName) => {
		const addressable = availableFields.find((f) => f.displayPath === newName);
		onChange(selectedFields.map((f) =>
			f.fieldName === oldName
				? {
					...newFieldConstraint(newName, addressable),
					// Carry the replaced row's requiredness, normalized to an
					// explicit boolean — copying a bare `undefined` through would
					// hand the new row semantics its checkbox does not show.
					required: normalizeRequiredness(f).required,
				}
				: f
		));
	};

	return (
		<div className="dsv-xallof-builder__branch-editor">
			{selectable.length === 0 ? (
				<p className="dsv-xallof-builder__hint">
					Add schema fields above before selecting branch fields.
				</p>
			) : (
				<>
					<p className="dsv-xallof-builder__hint">
						Select fields to apply constraints on when this branch is taken.
					</p>
					<div
						className="dsv-xallof-builder__checkboxes"
						data-testid={`xallof-rule-${ruleIndex}-${branch}-checkboxes`}
					>
						{Array.from(groups.entries()).map(([groupKey, entries]) => (
							<div
								key={`grp-${groupKey}`}
								className="dsv-xallof-builder__chip-group"
							>
								{entries.length > 1 && (
									<span className="dsv-xallof-builder__chip-group-label">
										{groupKey}
									</span>
								)}
								{entries.map((f) => {
									const isSelected = selectedNames.has(f.displayPath);
									return (
										<label
											key={f.displayPath}
											className={`dsv-xallof-builder__chip${isSelected ? ' dsv-xallof-builder__chip--selected' : ''}`}
										>
											<input
												type="checkbox"
												checked={isSelected}
												onChange={() => toggle(f.displayPath)}
												data-testid={`xallof-rule-${ruleIndex}-${branch}-${f.displayPath}-checkbox`}
											/>
											<span>{f.displayPath}</span>
										</label>
									);
								})}
							</div>
						))}
					</div>
				</>
			)}

			{selectedFields.length > 0 && (
				<div className="dsv-xallof-builder__field-grid">
					{atLeastOneCount > 0 && (
						<p
							className="dsv-xallof-builder__hint"
							data-testid={`xallof-rule-${ruleIndex}-${branch}-alo-hint`}
						>
							At least one of: {atLeastOneCount} fields — a submission must provide at
							least one of the fields marked &lsquo;at least one&rsquo;.
						</p>
					)}
					{selectedFields.map((f) => {
						const fieldErrors = errors?.[f.fieldName];
						if (!availableNames.has(f.fieldName)) {
							return (
								<DanglingRefRow
									key={f.fieldName}
									scope={`${branch}-field`}
									ruleIndex={ruleIndex}
									missingName={f.fieldName}
									compatibleFields={selectable}
									onReplace={(newName) => replaceField(f.fieldName, newName)}
									onRemove={() => removeField(f.fieldName)}
								/>
							);
						}
						const shown = normalizeRequiredness(f);
						// Advisory only — never blocks save (see getRuleWarnings).
						// A row that states value limits without requiring the field
						// looks identical to a required one apart from one checkbox,
						// which is exactly how the lift_datahub defect shipped.
						const warnings = getBranchFieldWarnings(shown);
						return (
							<div key={f.fieldName} className="dsv-xallof-builder__field-row">
								<FieldConstraintForm
									field={shown}
									branch={branch}
									ruleIndex={ruleIndex}
									errors={fieldErrors}
									onChange={updateField}
								/>
								{warnings.length > 0 && (
									<p
										className="dsv-xallof-builder__hint dsv-xallof-builder__hint--warning"
										role="status"
										aria-live="polite"
										data-testid={`xallof-rule-${ruleIndex}-${branch}-${f.fieldName}-required-warning`}
									>
										{warnings.map((w) => w.message).join(' ')}
									</p>
								)}
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
}

BranchFieldsEditor.propTypes = {
	branch: PropTypes.oneOf(['then', 'else']).isRequired,
	ruleIndex: PropTypes.number.isRequired,
	selectedFields: PropTypes.array.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.object,
	onChange: PropTypes.func.isRequired,
};
